import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, StatusBadge, relativeTime } from "@/components/common";
import ShiftStatusCard from "@/components/ShiftStatusCard";
import { Thermometer, SprayCan, ListChecks, ClipboardCheck, ShoppingCart, AlertTriangle, Activity as ActivityIcon, Loader2, Users, Clock, DoorClosed, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CLOSE_REASONS = [
  ["equipment_failure", "Equipment failure"], ["power_failure", "Power failure"],
  ["staff_shortage", "Staff shortage"], ["emergency", "Emergency"],
  ["weather", "Weather"], ["water_supply", "Water supply issue"], ["other", "Other"],
];

export default function Dashboard() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [org, setOrg] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venue, setVenue] = useState(localStorage.getItem("cc_site") || "all");
  const [onToday, setOnToday] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("equipment_failure");
  const [closeNote, setCloseNote] = useState("");
  const [closing, setClosing] = useState(false);

  const loadOnToday = (v) => {
    if (!has("view_rota")) { setOnToday(null); setCoverage(null); return; }
    api.get(`/workforce/on-today${v && v !== "all" ? `?site_id=${v}` : ""}`).then((r) => setOnToday(r.data)).catch(() => setOnToday(null));
    api.get(`/workforce/coverage-summary${v && v !== "all" ? `?site_id=${v}` : ""}`).then((r) => setCoverage(r.data)).catch(() => setCoverage(null));
  };

  const loadDash = (v) => api.get(`/dashboard${v && v !== "all" ? `?venue_id=${v}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/organisation").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/me/sites").then((r) => setVenues(r.data.sites || [])).catch(() => {});
    const onSite = () => setVenue(localStorage.getItem("cc_site") || "all");
    window.addEventListener("cc-site-changed", onSite);
    const onTasks = () => loadDash(localStorage.getItem("cc_site") || "all");
    window.addEventListener("cc-tasks-changed", onTasks);
    return () => {
      window.removeEventListener("cc-site-changed", onSite);
      window.removeEventListener("cc-tasks-changed", onTasks);
    };
  }, []);
  useEffect(() => { loadDash(venue); loadOnToday(venue); /* eslint-disable-next-line */ }, [venue]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand" /></div>;
  const isManager = user.role === "admin" || user.role === "manager";
  // Navigate only when the user holds the destination's view permission; otherwise
  // the summary item stays non-clickable. Site context lives in localStorage
  // (cc_site) and is read by each module, so plain navigation preserves it.
  const go = (perm, to) => (has(perm) ? () => navigate(to) : undefined);
  const clickable = (fn) => (fn ? {
    onClick: fn,
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } },
    role: "button", tabIndex: 0,
  } : {});
  const activityNav = has("view_activity") ? () => navigate("/activity") : null;
  const staffNav = has("view_staff") ? () => navigate("/staff") : null;
  // Dashboard venue chips must update the SHARED site context so it follows the
  // user into the module (mirrors Layout.changeSite).
  const changeVenue = (v) => {
    setVenue(v);
    if (v === "all") localStorage.removeItem("cc_site"); else localStorage.setItem("cc_site", v);
    window.dispatchEvent(new Event("cc-site-changed"));
  };

  const op = data.operating_status;
  const canClose = has("manage_site_operating_status") && venue && venue !== "all";
  const submitCloseToday = async () => {
    setClosing(true);
    try {
      await api.post(`/sites/${venue}/close-today`, { reason: closeReason, note: closeNote });
      toast.success("Site marked closed for today");
      setCloseOpen(false); setCloseNote("");
      loadDash(venue);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark site closed"); }
    finally { setClosing(false); }
  };
  const opBanner = () => {
    if (!op) return null;
    const map = {
      open: { tone: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle2,
              text: op.effective_open ? `Open today · ${op.effective_open}–${op.effective_close}` : "Open today" },
      before_opening: { tone: "bg-slate-50 border-slate-200 text-slate-600", icon: Clock,
              text: op.effective_open ? `Opens at ${op.effective_open}` : "Not open yet" },
      after_closing: { tone: "bg-slate-50 border-slate-200 text-slate-600", icon: Clock, text: "Closed for the day" },
      closed: { tone: "bg-slate-100 border-slate-200 text-slate-600", icon: DoorClosed, text: "Closed today — reporting not required" },
      planned_closure: { tone: "bg-amber-50 border-amber-200 text-amber-700", icon: DoorClosed,
              text: `Closed today — planned closure${op.reason ? ` (${op.reason})` : ""} · reporting not required` },
      unexpected_closure: { tone: "bg-red-50 border-red-200 text-red-700", icon: DoorClosed,
              text: `Closed today — emergency closure${op.reason ? ` (${op.reason})` : ""} · reporting not required` },
      open_exception: { tone: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle2, text: "Open today (special opening)" },
    };
    const m = map[op.status];
    if (!m) return null;
    const Icon = m.icon;
    return (
      <div className={`mb-5 flex items-center justify-between gap-3 border rounded-2xl p-4 ${m.tone}`} data-testid="operating-status-banner">
        <div className="flex items-center gap-3"><Icon className="shrink-0" size={20} /><div className="text-sm font-medium" data-testid="operating-status-text">{m.text}</div></div>
        {canClose && op.status !== "planned_closure" && op.status !== "unexpected_closure" && (
          <button onClick={() => setCloseOpen(true)} data-testid="mark-closed-today"
            className="shrink-0 text-sm font-semibold px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50">Mark site closed today</button>
        )}
      </div>
    );
  };

  const quick = [
    { to: "/temperatures", label: "Temperatures", icon: Thermometer, perm: "view_temperature", stat: `${data.temperatures.today} today` },
    { to: "/cleaning", label: "Cleaning", icon: SprayCan, perm: "view_cleaning", stat: `${data.cleaning.completed}/${data.cleaning.total}` },
    { to: "/prep", label: "Prep", icon: ListChecks, perm: "view_prep", stat: `${data.prep.outstanding} to do` },
    { to: "/checklists", label: "Checklists", icon: ClipboardCheck, perm: "view_checklists", stat: `${data.checklists.completed}/${data.checklists.total}` },
  ].filter((q) => has(q.perm));

  return (
    <div className="overflow-x-hidden">
      {org?.logo && <img src={`${API}/files/${org.logo}?auth=${localStorage.getItem("cc_token")}`} alt="logo" className="h-10 mb-3 object-contain" data-testid="dashboard-logo" />}
      <PageHeader title={`${greeting()}, ${user.name}`} subtitle="Here's what needs doing right now." testid="dashboard-header" />

      {venues.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center mb-5" data-testid="dashboard-venue-filter">
          <span className="text-xs font-semibold text-slate-500 uppercase">Venue</span>
          <button onClick={() => changeVenue("all")} className={`text-sm px-3 py-1 rounded-full ${venue === "all" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All sites</button>
          {venues.map((v) => <button key={v.venue_id} onClick={() => changeVenue(v.venue_id)} data-testid={`dash-venue-${v.venue_id}`} className={`text-sm px-3 py-1 rounded-full ${venue === v.venue_id ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{v.name}</button>)}
        </div>
      )}

      {opBanner()}

      <div className="mb-6" data-testid="dashboard-shift-card"><ShiftStatusCard /></div>

      {data.temperatures.exceptions > 0 && has("view_temperature") && (
        <div {...clickable(() => navigate("/temperatures"))} data-testid="exception-alert"
          className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 cursor-pointer hover:bg-red-100 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500">
          <AlertTriangle className="shrink-0" />
          <div className="text-sm font-medium">{data.temperatures.exceptions} temperature exception{data.temperatures.exceptions > 1 ? "s" : ""} need review.</div>
        </div>
      )}

      {/* Management summary */}
      {isManager && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 [&>*]:min-w-0" data-testid="management-summary">
          <StatCard testid="stat-cleaning" label="Cleaning" value={`${data.cleaning.completed}/${data.cleaning.total}`} sub="completed today" tone="success" icon={SprayCan} onClick={go("view_cleaning", "/cleaning")} />
          <StatCard testid="stat-temps" label="Temp checks" value={data.temperatures.passed} sub={`${data.temperatures.exceptions} exceptions`} tone={data.temperatures.exceptions ? "error" : "success"} icon={Thermometer} onClick={go("view_temperature", "/temperatures")} />
          <StatCard testid="stat-prep" label="Prep" value={data.prep.outstanding} sub="outstanding" tone={data.prep.outstanding ? "warning" : "success"} icon={ListChecks} onClick={go("view_prep", "/prep")} />
          <StatCard testid="stat-orders" label="Orders" value={data.orders.pending} sub={`${data.orders.sent} sent · ${data.orders.failed} failed`} tone={data.orders.failed ? "error" : "default"} icon={ShoppingCart} onClick={go("view_orders", "/orders")} />
        </div>
      )}

      {/* Who's on today */}
      {has("view_rota") && onToday && (
        <div className="mb-8" data-testid="whos-on-today">
          <h2 className="font-display text-lg font-bold text-slate-800 mb-3 flex items-center gap-2"><Users size={18} /> Who's on today
            <span className="text-xs font-medium text-slate-400">· {onToday.total} scheduled</span></h2>
          {coverage && (
            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="coverage-summary">
              <div className="cc-card p-3 text-center" data-testid="cov-scheduled"><div className="text-2xl font-bold text-slate-800">{coverage.today_scheduled}</div><div className="text-xs text-slate-500">Scheduled today</div></div>
              <div className={`cc-card p-3 text-center ${coverage.open_cover ? "ring-1 ring-amber-300" : ""}`} data-testid="cov-open"><div className={`text-2xl font-bold ${coverage.open_cover ? "text-amber-600" : "text-slate-800"}`}>{coverage.open_cover}</div><div className="text-xs text-slate-500">Open cover</div></div>
              <div className="cc-card p-3 text-center" data-testid="cov-pending"><div className="text-2xl font-bold text-slate-800">{coverage.pending_requests}</div><div className="text-xs text-slate-500">Pending requests</div></div>
            </div>
          )}
          {onToday.total === 0 ? (
            <div className="cc-card p-5 text-sm text-slate-400" data-testid="whos-on-empty">Nobody is scheduled on the published rota today.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {onToday.sites.map((s) => (
                <div key={s.site_id} className="cc-card p-4" data-testid={`whos-on-site-${s.site_id}`}>
                  <div className="text-xs font-semibold uppercase text-slate-400 mb-2">{s.site_name}</div>
                  <div className="space-y-2">
                    {s.people.map((p, i) => (
                      <div key={`${p.person_id}-${i}`} className="flex items-center justify-between" data-testid={`whos-on-person-${p.person_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="h-7 w-7 rounded-full bg-brand-light text-brand-dark text-xs font-bold flex items-center justify-center shrink-0">{(p.person_name || "?")[0]}</span>
                          <span className="text-sm font-medium text-slate-700 truncate">{p.person_name}</span>
                          {p.role ? <span className="text-xs text-slate-400 truncate">· {p.role}</span> : null}
                        </div>
                        <span className="text-xs text-slate-500 flex items-center gap-1 shrink-0"><Clock size={12} /> {p.start}–{p.end}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <h2 className="font-display text-lg font-bold text-slate-800 mb-3">Jump in</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 [&>*]:min-w-0">
        {quick.map((q) => (
          <button key={q.to} onClick={() => navigate(q.to)} data-testid={`quick-${q.label.toLowerCase()}`}
            className="cc-card p-5 text-left hover:border-brand hover:shadow-md transition-all">
            <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center mb-3"><q.icon className="text-brand-dark" size={22} /></div>
            <div className="font-semibold text-slate-800">{q.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{q.stat}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent activity */}
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800 mb-3 flex items-center gap-2"><ActivityIcon size={18} /> Recent activity</h2>
          <div className="cc-card divide-y divide-slate-100" data-testid="recent-activity">
            {data.recent_activity.length === 0 && <div className="p-5 text-sm text-slate-400">No activity yet.</div>}
            {data.recent_activity.map((a) => (
              <div key={a.log_id} {...clickable(activityNav)} data-testid={`activity-row-${a.log_id}`}
                className={`p-4 flex items-start gap-3 ${activityNav ? "cursor-pointer hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-link" : ""}`}>
                <div className="h-8 w-8 rounded-full bg-brand-light text-brand-dark text-xs font-bold flex items-center justify-center shrink-0">{(a.user_name || "?")[0]}</div>
                <div className="min-w-0">
                  <div className="text-sm text-slate-700">{a.summary}</div>
                  <div className="text-[11px] text-slate-400">{relativeTime(a.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Staff activity leaderboard */}
        {isManager && (
          <div>
            <h2 className="font-display text-lg font-bold text-slate-800 mb-3">Staff activity today</h2>
            <div className="cc-card p-4 space-y-3" data-testid="staff-activity">
              {data.staff_activity.length === 0 && <div className="text-sm text-slate-400">No actions logged today.</div>}
              {data.staff_activity.map((s) => (
                <div key={s.name} {...clickable(staffNav)} data-testid={`staff-row-${s.name}`}
                  className={`flex items-center gap-3 rounded-lg px-1 py-0.5 ${staffNav ? "cursor-pointer hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-link" : ""}`}>
                  <span className="text-sm font-medium text-slate-700 w-20 truncate">{s.name}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand rounded-full" style={{ width: `${Math.min(100, s.count * 12)}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-500 w-6 text-right">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {closeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="close-today-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-2 font-display font-bold text-slate-800"><DoorClosed size={18} className="text-brand" /> Mark site closed today</div>
            <p className="text-sm text-slate-500">Reporting requirements for today will be suspended and the closure will be recorded in the audit history.</p>
            <div>
              <label className="text-xs font-semibold text-slate-600">Reason</label>
              <select value={closeReason} onChange={(e) => setCloseReason(e.target.value)} data-testid="close-today-reason"
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {CLOSE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Note (optional)</label>
              <input value={closeNote} onChange={(e) => setCloseNote(e.target.value)} data-testid="close-today-note"
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Anything worth recording" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setCloseOpen(false)} data-testid="close-today-cancel">Cancel</Button>
              <Button onClick={submitCloseToday} disabled={closing} data-testid="close-today-confirm" className="bg-brand hover:bg-brand-hover">
                {closing ? <Loader2 className="animate-spin" size={16} /> : "Confirm closure"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}
